import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Modal, Alert, Space, Button, Spin, message } from 'antd';
import intl from 'react-intl-universal';
import useRoomCard from '@/hooks/useRoomCard';
import useUploadDoorLock, { CardUsage, UploadStatus } from '@/hooks/useUploadDoorLock';
import { formatDate } from '@/utils';
import { getTimeByUtcOffset } from '@/utils/intl';
import { DATE_TIME_FORMAT } from '@/config';
import { isNil } from '@/utils/number';
import * as CardApi from '@/api/card';

import { LockVendor } from './config';
import { UpdateCardProps } from './interface';
import { transformParamsC2PMS } from './helper';
import styles from './index.module.scss';

const UpdateCardModal: React.FC<UpdateCardProps> = ({
  updateModalVisibile,
  roomId,
  roomNo,
  enabledVendor,
  ...restProps
}) => {
  const defaultRoomInfo = {
    dai: 0,
    lockNo: '',
    checkInTime: '',
    checkOutTime: '',
    roomId: 0,
    roomNo: '',
  };

  const [hotelId, setHotelId] = useState(null);
  const [roomInfoLoading, setRoomInfoLoading] = useState(false);
  const [roomInfo, setRoomInfo] = useState(defaultRoomInfo);
  const [errorCard, setErrorCard] = useState(false);

  const {
    cardInstance,
    roomCardInfo,
    readError,
    reading,
    blankCard,
    resetCardStates,
    startRead,
    retryRead,
  } = useRoomCard({
    hotelId,
    vendorInfo: enabledVendor,
  });

  const { startUploadDoorInfo, endUploadDoorInfo } = useUploadDoorLock();

  // 获取酒店id(H21需要)
  const fetchHotelId = () => {
    return CardApi.fetchHotelId();
  };

  // 初始化房间信息
  const initRoomInfo = async () => {
    setRoomInfoLoading(true);
    try {
      const res = await CardApi.fetchRoomInfo({
        roomId,
      });

      setRoomInfoLoading(false);

      if (res && res.status) {
        setRoomInfo({
          dai: res.data?.dai,
          lockNo: res.data?.lockNo,
          checkInTime: res.data?.validityStartTime,
          checkOutTime: res.data?.validityEndTime,
          roomId: res.data?.roomId,
          roomNo: res.data?.roomNo,
        });
      }
    } catch (e) {
      setRoomInfoLoading(false);
      setRoomInfo(defaultRoomInfo);
    }
  };

  // 更新房卡信息
  const updateCardInfo = async (cardInfo) => {
    try {
      const cardNo = transformParamsC2PMS(enabledVendor?.lockCode, cardInfo).serialnumber;
      const oldCardNo = transformParamsC2PMS(enabledVendor?.lockCode, roomCardInfo).serialnumber;

      const res = await CardApi.updateRoomCard({
        roomId,
        hotelId: enabledVendor.lockCode === LockVendor.H21 ? cardInfo?.hotelid : undefined,
        cardNo,
        validityStartTime: roomInfo.checkInTime,
        validityEndTime: roomInfo.checkOutTime,
        oldCardNo,
      });

      if (res && res.status) {
        message.success(intl.get('cpm_cards_update_success'));
        if (restProps.updateCallBack && typeof restProps.updateCallBack === 'function')
          restProps.updateCallBack();
      } else {
        message.error(res.msg || intl.get('cpm_cards_info_update_fail'));
      }
    } catch (e) {
      setErrorCard(true);
      throw e;
    }

    return false;
  };

  // 写卡
  const writeCard = () => {
    if (cardInstance !== null && cardInstance.connected) {
      let params: any = {};
      if (enabledVendor.lockCode === LockVendor.H21) {
        params = {
          hotelid: hotelId,
          dai: roomInfo.dai,
          lockno: roomInfo.lockNo,
          // 续住后新的预离时间
          effecteddate: formatDate(roomInfo.checkInTime, 'YYMMDDHHmm'),
          expireddate: formatDate(roomInfo.checkOutTime, 'YYMMDDHHmm'),
        };
      }
      if (enabledVendor.lockCode === LockVendor.BD) {
        params = {
          lockno: roomInfo.lockNo,
          effecteddate: formatDate(roomInfo.checkInTime, 'YYMMDDHHmm'),
          expireddate: formatDate(roomInfo.checkOutTime, 'YYMMDDHHmm'),
          username: enabledVendor.dbRoot,
          password: enabledVendor.dbPass,
          isguest: 1,
        };
      }
      if (
        [LockVendor.YG, LockVendor.CHUANGJIA, LockVendor.PengZe].includes(enabledVendor.lockCode)
      ) {
        params = {
          lockno: roomInfo.lockNo,
          effecteddate: formatDate(roomInfo.checkInTime, 'YYMMDDHHmm'),
          expireddate: formatDate(roomInfo.checkOutTime, 'YYMMDDHHmm'),
        };
      }

      cardInstance
        .writeRoomCard(params)
        .then(async (data) => {
          await updateCardInfo(data);

          endUploadDoorInfo({ result: UploadStatus.Success });
        })
        .catch((e) => {
          // message.error(e.error || '写卡失败');
          setErrorCard(true);

          endUploadDoorInfo({ result: UploadStatus.Fail, remark: e.error || e.msg });
        });
    }
  };

  // 比对卡信息(H21)
  const compareCardInfo = (oldCard, cardDetail) => {
    // console.log('old==>>>new', oldCard, cardDetail);
    if (!oldCard || !cardDetail) return false;

    // H21有授权卡号
    let isSameHotelId = true;
    if (enabledVendor.lockCode === LockVendor.H21) {
      isSameHotelId = Number(cardDetail.hotelId) === oldCard.hotelid;
    }
    const oldCardNo = oldCard.serialnumber;

    if (
      isSameHotelId &&
      cardDetail.roomId === roomId &&
      cardDetail.cardNo === oldCardNo &&
      // pms上次预离时间和当前卡信息预离时间
      formatDate(cardDetail.validityEndTime, 'YYMMDDHHmm') === oldCard.expireddate
    ) {
      return true;
    }

    return false;
  };

  // 校验房卡(H21)
  const checkCardInfo = async (cardInfo) => {
    let isRightCard = false;
    try {
      // 获取房卡信息
      const { data, status } = await CardApi.fetchCardInfo({
        cardNo: cardInfo.serialnumber,
        hotelId: enabledVendor.lockCode === LockVendor.H21 ? cardInfo?.hotelid : undefined,
      });

      // 对比房卡信息
      if (status) {
        isRightCard = compareCardInfo(cardInfo, data);
      }
    } catch (e) {
      setErrorCard(true);

      endUploadDoorInfo({ result: UploadStatus.Fail, remark: e.msg });
    }

    if (!isRightCard) {
      setErrorCard(true);

      endUploadDoorInfo({
        result: UploadStatus.Fail,
        remark: intl.get('device_door_no_current_room_card'),
      });

      return false;
    }

    writeCard();
    return false;
  };

  const retry = () => {
    startUploadDoorInfo({ type: CardUsage.Update, roomNo });

    setErrorCard(false);
    retryRead();
  };

  const resetStates = () => {
    setErrorCard(false);
    setHotelId(null);
    setRoomInfo(defaultRoomInfo);
    resetCardStates();
  };

  const closeModal = () => {
    resetStates();
    if (restProps.updateCallBack && typeof restProps.updateCallBack === 'function')
      restProps.updateCallBack();
  };

  useEffect(() => {
    if (updateModalVisibile) {
      startUploadDoorInfo({ type: CardUsage.Update, roomNo });

      // H21需要先获取酒店id
      if (enabledVendor.lockCode === LockVendor.H21) {
        fetchHotelId().then((res) => {
          setHotelId(Number(res.data));
        });
      }

      initRoomInfo();
    } else {
      resetStates();
    }
  }, [updateModalVisibile]);

  useEffect(() => {
    if (enabledVendor.lockCode && !isNil(roomInfo.lockNo)) {
      if (enabledVendor.lockCode !== LockVendor.H21) {
        startRead();
      }

      if (enabledVendor.lockCode === LockVendor.H21 && !isNil(hotelId)) {
        startRead();
      }
    }
  }, [enabledVendor, hotelId, roomInfo]);

  // 成功读取到房卡
  useEffect(() => {
    const cardInfo = transformParamsC2PMS(enabledVendor?.lockCode, roomCardInfo);
    if (cardInfo && cardInfo.serialnumber) {
      checkCardInfo(cardInfo);
    }
  }, [roomCardInfo]);

  useEffect(() => {
    return () => {
      resetStates();
    };
  }, []);

  const modalFooter = () => {
    if (errorCard || readError || blankCard) {
      return (
        <Space align="start" size={16}>
          <Button type="primary" onClick={() => retry()}>
            {intl.get('app_confirm_retry_btn')}
          </Button>
          <Button onClick={() => closeModal()}>{intl.get('app_confirm_cancel_btn')}</Button>
        </Space>
      );
    }

    return <Button onClick={() => closeModal()}>{intl.get('app_confirm_cancel_btn')}</Button>;
  };

  const renderHint = () => {
    if (errorCard || readError || blankCard) {
      return <Alert message={intl.get('cpm_cards_abnormal_canot_update')} type="error" showIcon />;
    }

    return <Alert message={intl.get('cpm_cards_put_old_on_write_machine')} type="info" showIcon />;
  };

  return (
    <Modal
      width={560}
      title={intl.get('cpm_cards_update')}
      visible={updateModalVisibile}
      maskClosable={false}
      keyboard={false}
      footer={modalFooter()}
      destroyOnClose
      onCancel={() => closeModal()}
    >
      {renderHint()}
      <Spin spinning={roomInfoLoading || reading}>
        <ul className={styles.cardInfo}>
          <li>
            <label>{intl.get('app_room')}</label>
            <span>{roomInfo.roomNo || '-'}</span>
          </li>
          <li>
            <label>{intl.get('device_lock_number')}</label>
            <span>{roomInfo.lockNo || '-'}</span>
          </li>
          <li>
            <label>{intl.get('manage_license_field_start_label')}</label>
            <span>{getTimeByUtcOffset(roomInfo.checkInTime, DATE_TIME_FORMAT) || '-'}</span>
          </li>
          <li>
            <label>{intl.get('cpm_cards_end_validity_period')}</label>
            <span>{getTimeByUtcOffset(roomInfo.checkOutTime, DATE_TIME_FORMAT) || '-'}</span>
          </li>
        </ul>
      </Spin>
    </Modal>
  );
};

const mapStateToProps = (state) => {
  const { roomCardEnabled, enabledVendor } = state.card;
  return { roomCardEnabled, enabledVendor };
};

export default connect(mapStateToProps, null)(UpdateCardModal);
